/**
 * QLF unified search — powers both the predictive drawer and the search results
 * page from ONE module, backed by Meilisearch via InstantSearch.js.
 *
 * Replaces the old duplicated qlf-main-search.js + predictive-search.js.
 *
 * Config comes from window.theme.search (set in layout/theme.liquid):
 *   { host, apiKey, productsIndex, instantsearchUrl, instantMeilisearchUrl }
 *
 * The heavy InstantSearch libraries are lazy-loaded on first search interaction.
 */
(function () {
  ('use strict');

  var cfg = (window.theme && window.theme.search) || {};
  if (!cfg.host || !cfg.apiKey || !cfg.productsIndex) {
    return; // not configured — stay silent
  }

  var PAGE_HITS = 24;
  var DRAWER_HITS = PAGE_HITS;
  var ARTICLE_HITS = 6;

  // ---------------------------------------------------------------- helpers
  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this,
        args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function getCurrencySymbol(code) {
    try {
      var parts = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: code
      }).formatToParts(1);
      var c = parts.find(function (p) {
        return p.type === 'currency';
      });
      return c ? c.value : code;
    } catch (e) {
      return code;
    }
  }

  function imgResize(url, size) {
    if (!url) return '';
    var q = '',
      src = url;
    var qi = url.indexOf('?');
    if (qi !== -1) {
      q = url.slice(qi);
      src = url.slice(0, qi);
    }
    var dot = src.lastIndexOf('.');
    if (dot === -1) return url;
    return src.slice(0, dot) + '_' + size + 'x' + src.slice(dot) + q;
  }

  function numberWithCommas(x) {
    return String(x).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  function isTruthy(value) {
    if (value === true || value === 1) return true;
    if (typeof value === 'string') {
      var normalized = value.trim().toLowerCase();
      return (
        normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'ja'
      );
    }
    return false;
  }

  function normalizeSearchText(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function queryTerms(query) {
    var normalized = normalizeSearchText(query);
    if (!normalized) return [];
    return normalized.split(/\s+/).filter(function (term) {
      return term.length > 1;
    });
  }

  function filterNegativeHits(hits, query) {
    var terms = queryTerms(query);
    if (!terms.length) return hits;
    var phrase = normalizeSearchText(query);

    return hits.filter(function (hit) {
      var negative = normalizeSearchText(hit.negative_search_terms);
      if (!negative) return true;
      if (phrase && negative.indexOf(phrase) !== -1) return false;
      return !terms.some(function (term) {
        return negative.split(/\s+/).indexOf(term) !== -1;
      });
    });
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function hasTag(hit, tag) {
    var tags = Array.isArray(hit.tags) ? hit.tags : String(hit.tags || '').split(',');
    return tags.some(function (value) {
      return value.trim().toLowerCase() === tag.toLowerCase();
    });
  }

  function isBundleHit(hit) {
    return hasTag(hit, 'QLF Bundle') || hasTag(hit, 'AddToCartRedirect');
  }

  // Products tagged `disable-atc` are not purchasable (mirrors
  // snippets/product-quick-view-button.liquid on native cards): no add-to-cart and
  // no variant quick-view/dropdown — just a disabled "Nicht verfügbar" button.
  function isDisableAtc(hit) {
    return hasTag(hit, 'disable-atc');
  }

  function variantIdFromUrl(url) {
    try {
      return new URL(url, window.location.origin).searchParams.get('variant') || '';
    } catch (e) {
      var match = String(url || '').match(/[?&]variant=([^&]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    }
  }

  function isVariantHit(hit) {
    var variantId = variantIdFromUrl(hit.product_url);
    return !!variantId && String(hit.shopify_product_id || '') === String(variantId);
  }

  function cartIconHtml() {
    return (
      '<span class="single-add-to-cart-button--text"><svg width="24" height="20"><use href="#cart-icon"></use></svg></span>' +
      '<span class="predictive-product-card__add-success" aria-hidden="true"></span>' +
      '<span class="loading-overlay"><svg aria-hidden="true" focusable="false" role="presentation" class="spinner"><use href="#spinner"></use></svg></span>'
    );
  }

  function directAddToCartButton(variantId, extraClasses) {
    return (
      '<button type="button" name="addToCart" data-productid="' +
      escapeHtml(variantId) +
      '" ' +
      'class="product-card--add-to-cart--button product-card--add-to-cart-button1 single-add-to-cart-button ' +
      (extraClasses || '') +
      '">' +
      cartIconHtml() +
      '</button>'
    );
  }

  function resolvingAddToCartButton(handle, variantId, extraClasses) {
    return (
      '<button type="button" name="addToCart" data-product-handle="' +
      escapeHtml(handle) +
      '" data-fallback-variant-id="' +
      escapeHtml(variantId) +
      '" ' +
      'class="product-card--add-to-cart--button product-card--add-to-cart-button1 single-add-to-cart-button ' +
      (extraClasses || '') +
      '">' +
      cartIconHtml() +
      '</button>'
    );
  }

  function quickViewButton(variantId, handle, extraClasses) {
    return (
      '<quick-view class="product-card--add-to-cart--button product-card--add-to-cart-button ' +
      (extraClasses || '') +
      '" ' +
      'data-product-id="' +
      escapeHtml(variantId) +
      '" data-product-handle="' +
      escapeHtml(handle) +
      '" tabindex="-1">' +
      cartIconHtml() +
      '</quick-view>'
    );
  }

  // Disabled add-to-cart button for `disable-atc` products: shows the text
  // "Nicht verfügbar" and carries no variant selection (no dropdown/quick-view).
  function unavailableButton(extraClasses) {
    return (
      '<button type="button" disabled aria-disabled="true" ' +
      'class="product-card--add-to-cart--button single-add-to-cart-button product-card--add-to-cart-button--unavailable ' +
      (extraClasses || '') +
      '">' +
      '<span class="single-add-to-cart-button--text">Nicht verfügbar</span>' +
      '</button>'
    );
  }

  function fetchProduct(handle) {
    return fetch(
      (window.Shopify ? Shopify.routes.root : '/') +
        'products/' +
        encodeURIComponent(handle) +
        '.js'
    ).then(function (res) {
      if (!res.ok) throw new Error('Product JSON failed');
      return res.json();
    });
  }

  var productJsonCache = {};
  function fetchProductCached(handle) {
    if (!productJsonCache[handle]) productJsonCache[handle] = fetchProduct(handle);
    return productJsonCache[handle];
  }

  function variantTypeLabel(product, variantId) {
    if (!product || !Array.isArray(product.variants)) return '';
    var variant = product.variants.find(function (item) {
      return String(item.id) === String(variantId);
    });
    if (!variant) return '';
    var options = [variant.option1, variant.option2, variant.option3].filter(Boolean);
    var categoryIndex = -1;
    if (Array.isArray(product.options)) {
      categoryIndex = product.options.findIndex(function (option) {
        return /kategorien|kategorie|category|typ|type|packung|größe|groesse/i.test(
          option.name || ''
        );
      });
    }
    var preferred = categoryIndex >= 0 ? options[categoryIndex] : '';
    var label =
      preferred ||
      options.find(function (option) {
        return /sample|probe|probierbox/i.test(option);
      }) ||
      '';
    var normalizedLabel = label.trim();

    if (!normalizedLabel || /^(1(?:\s|$)|default title$)/i.test(normalizedLabel)) {
      return '';
    }

    return normalizedLabel;
  }
  // Split "Base – Flavour" titles on the first spaced dash (hyphen / en / em dash).
  // Any further spaced dashes inside the flavour part (e.g. "Haselnussschnitte -
  // 12er Pack") are normalised to a middot separator.
  function splitTitleFlavor(title) {
    var m = String(title || '').match(/^(.*?)\s+[-–—]\s+(.+)$/);

    if (!m) {
      return {
        base: title,
        flavor: ''
      };
    }

    var flavor = m[2].trim().replace(/\s+[-–—]\s+/g, ' · ');

    // Entfernt " · 1 Dose", " · 1 Beutel", " · 1 Flasche", ...
    flavor = flavor.replace(/\s*·\s*1\s+\S+$/i, '');

    return {
      base: m[1].trim(),
      flavor: flavor
    };
  }

  function enrichVariantLabels(root) {
    root
      .querySelectorAll('[data-variant-label-handle][data-variant-label-id]')
      .forEach(function (el) {
        if (el.dataset.variantLabelLoaded === 'true') return;
        el.dataset.variantLabelLoaded = 'true';
        var handle = el.dataset.variantLabelHandle;
        var variantId = el.dataset.variantLabelId;
        var labelOnly = el.dataset.variantLabelOnly === 'true';
        fetchProductCached(handle)
          .then(function (product) {
            var label = variantTypeLabel(product, variantId);
            if (!label) return;
            if (labelOnly) {
              // Show the flavour (Geschmack) on its own line, not appended to the title.
              // Combine the flavour from the title with the pack-size variant label,
              // e.g. "Double Chocolate" + "12er Pack" → "Double Chocolate · 12er Pack".
              // Skip the label if it already appears in the title flavour (e.g.
              // "Probierbox" + "Probierbox" → just "Probierbox").
              var prefix = el.dataset.variantLabelPrefix || '';
              var parts = prefix ? prefix.split(' · ') : [];
              var exists = parts.some(function (p) {
                return p.trim().toLowerCase() === label.trim().toLowerCase();
              });
              if (!exists) parts.push(label);
              el.textContent = parts.join(' · ');
              el.hidden = false;
            } else {
              var base = el.dataset.variantLabelBase || el.textContent.trim();
              el.textContent = base + ' (' + label + ')';
            }
          })
          .catch(function () {});
      });
  }

  function openQuickViewFromButton(button) {
    var handle = button.dataset.productHandle;
    var variantId = button.dataset.fallbackVariantId || '';
    var quickView = document.createElement('quick-view');
    quickView.className = 'product-card--add-to-cart--button product-card--add-to-cart-button';
    quickView.dataset.productHandle = handle;
    quickView.dataset.productId = variantId;
    quickView.style.display = 'none';
    quickView.innerHTML = cartIconHtml();
    document.body.appendChild(quickView);
    setTimeout(function () {
      quickView.click();
      setTimeout(function () {
        quickView.remove();
      }, 1000);
    }, 0);
  }

  // ------------------------------------------- back-in-stock notify (search cards)
  // A small round bell button on sold-out search cards: it takes the add-to-cart
  // button's spot on the results-page card (the "Ausverkauft" badge stays), and
  // replaces the sold-out label in the predictive drawer. Clicking it opens a
  // shared popup where the visitor enters an email to be notified once the product
  // is back — submitted to the same Klaviyo back-in-stock endpoint used by
  // snippets/qlf-back-in-stock-opt-in.liquid.
  var BIS = (function () {
    var SITE_ID = 'UwJXKs';
    var API_URL =
      'https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=' +
      encodeURIComponent(SITE_ID);
    var API_REVISION = '2025-07-15';
    var LS_KEY = 'bis_subscriptions_v1'; // shared with the product-page opt-in snippet

    function normalizeVariantId(raw) {
      if (!raw) return null;
      var s = String(raw);
      var m = s.match(/(\d+)(?!.*\d)/);
      return m ? m[1] : s;
    }

    function readStore() {
      try {
        return JSON.parse(localStorage.getItem(LS_KEY) || '{}') || {};
      } catch (e) {
        return {};
      }
    }
    function remember(email, variantId) {
      try {
        var vid = normalizeVariantId(variantId);
        if (!vid || !email) return;
        var data = readStore();
        data[email + '::' + vid] = true;
        localStorage.setItem(LS_KEY, JSON.stringify(data));
      } catch (e) {
        /* ignore */
      }
    }
    function isRemembered(email, variantId) {
      var vid = normalizeVariantId(variantId);
      if (!vid || !email) return false;
      return !!readStore()[email + '::' + vid];
    }

    var modal = null,
      els = null,
      current = null;

    function build() {
      if (modal) return;
      modal = document.createElement('div');
      modal.id = 'qlf-bis-modal';
      modal.className = 'qlf-bis-modal';
      modal.hidden = true;
      modal.innerHTML =
        '<div class="qlf-bis-modal__overlay" data-bis-close></div>' +
        '<div class="qlf-bis-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="qlf-bis-title">' +
        '<button type="button" class="qlf-bis-modal__close" aria-label="Schließen" data-bis-close>&times;</button>' +
        '<p class="qlf-bis-modal__product"></p>' +
        '<p class="qlf-bis-modal__intro">Trag dich hier ein und wir informieren dich als Erstes, sobald dein Produkt wieder auf Lager ist.</p>' +
        '<form class="qlf-bis-modal__form qlf-flex qlf-gap-2">' +
        '<label for="qlf-bis-email" class="sr-only">Deine E-Mail-Adresse</label>' +
        '<input type="email" id="qlf-bis-email" class="qlf-w-full" placeholder="Deine E-Mail-Adresse" required>' +
        '<button type="submit" class="button accent">Benachrichtige mich</button>' +
        '</form>' +
        '<p class="qlf-bis-modal__msg" hidden></p>' +
        '</div>';
      document.body.appendChild(modal);

      els = {
        dialog: modal.querySelector('.qlf-bis-modal__dialog'),
        product: modal.querySelector('.qlf-bis-modal__product'),
        intro: modal.querySelector('.qlf-bis-modal__intro'),
        form: modal.querySelector('.qlf-bis-modal__form'),
        email: modal.querySelector('#qlf-bis-email'),
        submit: modal.querySelector('.qlf-bis-modal__form button[type="submit"]'),
        msg: modal.querySelector('.qlf-bis-modal__msg')
      };

      modal.addEventListener('click', function (e) {
        if (e.target.closest('[data-bis-close]')) close();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !modal.hidden) close();
      });
      els.form.addEventListener('submit', function (e) {
        e.preventDefault();
        onSubmit();
      });
    }

    function showMsg(text, ok) {
      els.msg.hidden = false;
      els.msg.textContent = text;
      els.msg.style.color = ok ? '#279a4b' : '#c62a32';
    }

    function setSubscribedUI(subscribed) {
      els.submit.disabled = subscribed;
      els.submit.textContent = subscribed ? 'Benachrichtigung vorgemerkt' : 'Benachrichtige mich';
      els.form.style.display = subscribed ? 'none' : '';
      els.intro.style.display = subscribed ? 'none' : '';
    }

    function open(data) {
      build();
      current = data;
      els.product.textContent = data.title || '';
      els.product.style.display = data.title ? '' : 'none';
      els.msg.hidden = true;
      var email = (cfg.customerEmail || '').trim();
      if (email) els.email.value = email;
      // Show "already subscribed" state when we have an email + remembered variant.
      var already = data.isVariant && email && isRemembered(email, data.variant);
      setSubscribedUI(!!already);
      modal.hidden = false;
      document.body.classList.add('qlf-bis-open');
      setTimeout(function () {
        if (!els.email.value) els.email.focus();
      }, 30);
    }

    function close() {
      if (!modal) return;
      modal.hidden = true;
      document.body.classList.remove('qlf-bis-open');
    }

    function postKlaviyo(email, variantId) {
      var klaviyoVariantId = '$shopify:::$default:::' + normalizeVariantId(variantId);
      var payload = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: { email: email } } },
            channels: ['EMAIL']
          },
          relationships: {
            variant: { data: { type: 'catalog-variant', id: klaviyoVariantId } }
          }
        }
      };
      return fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: API_REVISION },
        body: JSON.stringify(payload)
      });
    }

    // Search hits may be product-level (no specific variant). Resolve the variant
    // to subscribe to: a direct variant hit uses its own id; otherwise fetch the
    // product JSON and pick the first variant (single-variant products are exact;
    // for fully sold-out multi-variant products the first variant is a sensible
    // default).
    function resolveVariantId(data) {
      if (data.isVariant && data.variant) return Promise.resolve(data.variant);
      if (!data.handle) return Promise.resolve(data.variant || null);
      return fetchProductCached(data.handle)
        .then(function (product) {
          var variants = (product && product.variants) || [];
          return (variants[0] && variants[0].id) || data.variant || null;
        })
        .catch(function () {
          return data.variant || null;
        });
    }

    function onSubmit() {
      var email = (els.email.value || '').trim();
      if (!email) {
        showMsg('Bitte gib deine E-Mail-Adresse ein.');
        return;
      }
      els.submit.disabled = true;
      els.submit.textContent = 'Wird gesendet …';
      var data = current;
      resolveVariantId(data)
        .then(function (variantId) {
          if (!variantId) throw new Error('no variant');
          return postKlaviyo(email, variantId).then(function (res) {
            if (res.status === 202) {
              remember(email, variantId);
              showMsg(
                'Alles klar! Wir benachrichtigen dich, sobald dieser Artikel wieder verfügbar ist.',
                true
              );
              setSubscribedUI(true);
            } else {
              return res
                .json()
                .catch(function () {
                  return {};
                })
                .then(function (err) {
                  var detail = (
                    (err && err.errors && err.errors[0] && err.errors[0].detail) ||
                    ''
                  ).toLowerCase();
                  if (
                    res.status === 409 ||
                    detail.indexOf('already') > -1 ||
                    detail.indexOf('duplicate') > -1 ||
                    detail.indexOf('conflict') > -1
                  ) {
                    remember(email, variantId);
                    showMsg(
                      'Du bist bereits eingetragen. Wir melden uns, sobald der Artikel verfügbar ist.',
                      true
                    );
                    setSubscribedUI(true);
                  } else {
                    showMsg(
                      (err && err.errors && err.errors[0] && err.errors[0].detail) ||
                        'Ups – das hat nicht geklappt. Bitte später noch einmal versuchen.'
                    );
                    els.submit.disabled = false;
                    els.submit.textContent = 'Benachrichtige mich';
                  }
                });
            }
          });
        })
        .catch(function () {
          showMsg('Netzwerkfehler. Bitte später erneut probieren.');
          els.submit.disabled = false;
          els.submit.textContent = 'Benachrichtige mich';
        });
    }

    var bound = false;
    function bind() {
      if (bound) return;
      bound = true;
      document.addEventListener('click', function (e) {
        var btn = e.target.closest && e.target.closest('.qlf-bis-btn');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        open({
          title: btn.getAttribute('data-bis-title') || '',
          handle: btn.getAttribute('data-bis-handle') || '',
          variant: btn.getAttribute('data-bis-variant') || '',
          isVariant: btn.getAttribute('data-bis-is-variant') === 'true'
        });
      });
    }

    return { bind: bind };
  })();

  function bellIcon() {
    return (
      '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"></path>' +
      '<path d="M13.73 21a2 2 0 0 1-3.46 0"></path>' +
      '</svg>'
    );
  }

  // Round "notify me when back in stock" button for sold-out search cards.
  // Carries the data BIS.open() needs.
  function bisButton(hit, extraClasses) {
    var variantId = hit.shopify_product_id;
    var url = hit.product_url || '';
    var handle = url.split('products/')[1] ? url.split('products/')[1].split('?')[0] : '';
    var label = 'Benachrichtigen, wenn wieder verfügbar';
    return (
      '<button type="button" class="qlf-bis-btn ' +
      (extraClasses || '') +
      '" ' +
      'data-bis-handle="' +
      escapeHtml(handle) +
      '" ' +
      'data-bis-variant="' +
      escapeHtml(variantId) +
      '" ' +
      'data-bis-is-variant="' +
      (isVariantHit(hit) ? 'true' : 'false') +
      '" ' +
      'data-bis-title="' +
      escapeHtml(hit.title || '') +
      '" ' +
      'aria-label="' +
      label +
      '" title="' +
      label +
      '">' +
      bellIcon() +
      '</button>'
    );
  }

  function renderStars(rating) {
    var r = parseFloat(rating) || 0;
    var full = Math.floor(r);
    var half = r % 1 > 0 ? 1 : 0;
    var empty = 5 - full - half;
    var html = '<span class="qlf-flex">';
    for (var i = 0; i < full; i++)
      html += '<svg class="qlf-w-4 qlf-h-4"><use href="#star" fill="#F6B812"></use></svg>';
    if (half) html += '<svg class="qlf-w-4 qlf-h-4"><use href="#half-star"></use></svg>';
    for (var j = 0; j < empty; j++)
      html += '<svg class="qlf-w-4 qlf-h-4"><use href="#star" fill="#F6DCA0"></use></svg>';
    return html + '</span>';
  }

  // ------------------------------------------------------- product card (single source of truth)
  // Mirrors snippets/predictive-product-card.liquid so the theme CSS styles it
  // identically to the native predictive cards — but rendered instantly from Meili.
  function productCard(hit) {
    var currency = getCurrencySymbol(
      (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR'
    );
    var productId = hit.parent_product_id || hit.shopify_product_id; // Shopify product id
    var variantId = hit.shopify_product_id; // variant id for split docs
    var url = hit.product_url || '';
    var image = imgResize(hit.product_image, 140);
    var title = hit.title || '';
    var type = hit.product_type || '';
    var price = hit.priceRangeV2;
    var compareAt = hit.compareAtPriceRange;
    var handle = url.split('products/')[1] ? url.split('products/')[1].split('?')[0] : '';
    var tags = Array.isArray(hit.tags) ? hit.tags.join(',') : hit.tags || '';
    var useDatora = ((window.theme && window.theme.productCard) || {}).datora !== false;
    var isBundle = isBundleHit(hit);
    var isSoldOut = hit.outofstock && !isBundle;

    function money(v) {
      return parseFloat(v).toFixed(2).replace('.', ',') + ' ' + currency;
    }

    // rating — only when there are reviews (matches predictive-product-card.liquid)
    var ratingStr = hit.trustedshops_product_rating || '';
    var ratingValue = ratingStr.split(',')[0] || '';
    var ratingCount = parseInt((ratingStr.split(',')[1] || '0').trim(), 10) || 0;
    var ratingHtml =
      ratingCount > 0
        ? '<a href="' +
          url +
          '#productreviews" class="predictive-product-card__rating">' +
          renderStars(ratingValue) +
          '<span>' +
          numberWithCommas(ratingCount) +
          '</span></a>'
        : '';

    // price — same .price/.amount markup as snippets/product-price.liquid
    var priceHtml;
    if (compareAt && parseFloat(compareAt) > parseFloat(price)) {
      priceHtml =
        '<span class="price"><span class="price--price-details price--discounted-price-details">' +
        '<del><span class="amount from">' +
        money(compareAt) +
        '</span></del>' +
        '<ins><span class="amount discounted">' +
        money(price) +
        '</span></ins>' +
        '<span class="price-saving"><span class="price-saving-text">Spare</span><span class="price-saving-small">Spare</span> ' +
        money(compareAt - price) +
        '</span>' +
        '</span></span>';
    } else {
      priceHtml =
        '<span class="price"><span class="price--price-details"><span class="amount">' +
        money(price) +
        '</span></span></span>';
    }

    var priceCents = Math.round(parseFloat(price) * 100) || '';
    var compareCents = compareAt ? Math.round(parseFloat(compareAt) * 100) : '';
    var priceBlock = useDatora
      ? '<div class="predictive-product-card__datora-wrapper" data-datora-pid="' +
        productId +
        '" data-datora-vid="' +
        variantId +
        '" data-datora-tags="' +
        escapeHtml(tags) +
        '" data-datora-price="' +
        priceCents +
        '" data-datora-compare="' +
        compareCents +
        '" data-datora-spgid="false">' +
        priceHtml +
        '<div class="datora-card-badge" data-datora hidden></div>' +
        '</div>'
      : priceHtml;

    var typeHtml = type
      ? '<div class="predictive-product-card__description">' + escapeHtml(type) + '</div>'
      : '';

    // Same as the results card: show only the base title, with the flavour (Geschmack)
    // on its own grey line below it (enrichVariantLabels appends the pack-size label).
    var titleParts = splitTitleFlavor(title);
    var baseTitle = titleParts.base;
    var flavorFromTitle = titleParts.flavor;
    var flavorHtml =
      '<div class="predictive-product-card__flavor product-card-flavor" data-variant-label-handle="' +
      escapeHtml(handle) +
      '" data-variant-label-id="' +
      escapeHtml(variantId) +
      '" data-variant-label-only="true" data-variant-label-prefix="' +
      escapeHtml(flavorFromTitle) +
      '"' +
      (flavorFromTitle ? '' : ' hidden') +
      '>' +
      escapeHtml(flavorFromTitle) +
      '</div>';

    // action — render the <quick-view> custom element; on click app.js fetches the real
    // product page (?view=quick-view) into the Product-Drawer, so add-to-cart, variant
    // selection, multipacks and bundles all run through the native product logic.
    // The icon MUST sit in .single-add-to-cart-button--text: app.js toggles its display
    // on click (a bare <svg> there throws and the button does nothing).
    var actionHtml = '<div class="predictive-product-card__action">';
    if (isDisableAtc(hit)) {
      actionHtml += unavailableButton('product-card--add-to-cart-button-simple');
    } else if (isSoldOut) {
      actionHtml += bisButton(hit, 'qlf-bis-btn--action');
    } else if (handle) {
      actionHtml += isVariantHit(hit)
        ? '<div class="qlf-add-to-cart">' +
          directAddToCartButton(variantId, 'product-card--add-to-cart-button-simple') +
          '</div>'
        : isBundle
          ? quickViewButton(variantId, handle, 'product-card--add-to-cart-button-simple')
          : '<div class="qlf-add-to-cart qlf-add-to-cart--resolve">' +
            resolvingAddToCartButton(handle, variantId, 'product-card--add-to-cart-button-simple') +
            '</div>';
    }
    actionHtml += '</div>';

    return (
      '<div class="predictive-product-card" data-product-id="' +
      productId +
      '" data-variant-id="' +
      variantId +
      '" data-collection-ids="' +
      (hit.collection_ids || '') +
      '">' +
      '<a href="' +
      url +
      '" class="predictive-product-card__image" title="' +
      escapeHtml(title) +
      '">' +
      '<img src="' +
      image +
      '" alt="' +
      escapeHtml(title) +
      '" width="140" height="140" loading="lazy"></a>' +
      '<div class="predictive-product-card__info">' +
      '<a href="' +
      url +
      '" class="predictive-product-card__title" title="' +
      escapeHtml(title) +
      '">' +
      escapeHtml(baseTitle) +
      '</a>' +
      flavorHtml +
      ratingHtml +
      typeHtml +
      '<div class="predictive-product-card__price">' +
      priceBlock +
      '</div>' +
      '</div>' +
      actionHtml +
      '</div>'
    );
  }

  // Collection-style card (snippets/product-card.liquid look) — used on the search
  // RESULTS page so it matches collection grids. <product-card> is null-safe in app.js.
  // ---- search-card badges & tag (mirror product-card-badge.liquid / product-card-tag.liquid)
  function productBadges(hit, money) {
    var pc = (window.theme && window.theme.productCard) || {};
    var pos = pc.badgePosition || 'top right';
    var s = pc.strings || {};
    var price = parseFloat(hit.priceRangeV2);
    var compareAt = parseFloat(hit.compareAtPriceRange);
    var isBundle = isBundleHit(hit);
    var out = '';

    if (isTruthy(hit.vegan)) {
      out +=
        '<span class="badge ' +
        pos +
        '" style="background-color:#c4e5ab;color:#58793e;">Vegan</span>';
    }
    // Sale badge only when discounted AND in stock (matches the Liquid).
    if (compareAt > price && (!hit.outofstock || isBundle)) {
      var label;
      if (pc.saleBadgeType === 'sale') {
        label = s.onSale || 'Sale';
      } else {
        var amount =
          pc.saleBadgeType === 'save_amount'
            ? money(compareAt - price)
            : Math.round(((compareAt - price) * 100) / compareAt) + '%';
        label = (s.save || 'Spare __AMT__').replace('__AMT__', amount);
      }
      out += '<span class="badge onsale ' + pos + '">' + escapeHtml(label) + '</span>';
    }
    // Sold-out badge last so it sits to the right of the other badges (e.g. Vegan).
    if (hit.outofstock && !isBundle) {
      out +=
        '<span class="badge out-of-stock ' +
        pos +
        '">' +
        escapeHtml(s.soldOut || 'Ausverkauft') +
        '</span>';
    }

    return out
      ? '<span class="qlf-flex qlf-absolute badge--container" style="z-index:30;">' +
          out +
          '</span>'
      : '';
  }

  // Custom badges (badges.badge metaobjects), mirroring snippets/product-card-tag.liquid
  // → product-badge-item.liquid. The index provides hit.badges as a list of
  // {text, bg, fg, bc}; only card-context badges are included (position
  // "Produktseite"/"Multipackauswahl" are dropped server-side, see qlf-control-app).
  // The "Neu" badge is removed server-side after 6 weeks, so no client-side expiry
  // logic is needed here.
  function productCardBadges(hit) {
    var list = hit.badges;
    if (!Array.isArray(list) || !list.length) return '';

    var out = '';
    for (var i = 0; i < list.length; i++) {
      var b = list[i];
      if (!b || !b.text) continue;
      // Optional border, mirroring product-badge-item.liquid: rendered only when
      // the metaobject's bordercolor field is set.
      var border = b.bc ? ';border:1px solid ' + b.bc : '';
      out +=
        '<span class="product-card-tag" style="background-color:' +
        (b.bg || '#d7802f') +
        ';color:' +
        (b.fg || '#ffffff') +
        border +
        ';">' +
        escapeHtml(b.text) +
        '</span>';
    }

    return out ? '<div class="product-card-tags">' + out + '</div>' : '';
  }

  function productCardFull(hit) {
    // Settings mirrored from product-card.liquid (set in layout/theme.liquid) so the
    // search grid matches native collection cards (border, spacing, ratio, price pos).
    var pc = (window.theme && window.theme.productCard) || {};
    var alignment = pc.alignment || 'left';
    var aspect = pc.aspectRatio || 'square';
    var priceBefore = pc.pricePosition === 'before-product-title';
    var useDatora = pc.datora !== false;
    var cardClasses =
      'product-card text-' +
      alignment +
      ' product-card--content-spacing-' +
      (pc.contentSpacing === false ? 'false' : 'true') +
      ' product-card--border-' +
      (pc.border ? 'true' : 'false') +
      ' has-shadow--' +
      (pc.shadow ? 'true' : 'false');

    var currency = getCurrencySymbol(
      (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR'
    );
    var productId = hit.parent_product_id || hit.shopify_product_id;
    var variantId = hit.shopify_product_id;
    var url = hit.product_url || '';
    var image = imgResize(hit.product_image, 540);
    var title = hit.title || '';
    var type = hit.product_type || '';
    var price = hit.priceRangeV2;
    var compareAt = hit.compareAtPriceRange;
    var tags = Array.isArray(hit.tags) ? hit.tags.join(',') : hit.tags || '';
    var handle = url.split('products/')[1] ? url.split('products/')[1].split('?')[0] : '';

    function money(v) {
      return parseFloat(v).toFixed(2).replace('.', ',') + ' ' + currency;
    }

    var ratingStr = hit.trustedshops_product_rating || '';
    var ratingValue = ratingStr.split(',')[0] || '';
    var ratingCount = parseInt((ratingStr.split(',')[1] || '0').trim(), 10) || 0;
    var ratingHtml =
      ratingCount > 0
        ? '<a href="' +
          url +
          '#productreviews" class="qlf-ts-rating"><div class="qlf-ts-rating__stars">' +
          renderStars(ratingValue) +
          '<div class="qlf-ts-rating__count"><span class="qlf-ts-rating__count_number">' +
          numberWithCommas(ratingCount) +
          '</span> ' +
          '<span class="qlf-ts-rating-text">' +
          (ratingCount === 1 ? 'Bewertung' : 'Bewertungen') +
          '</span></div></div></a>'
        : '';

    var priceHtml;
    if (compareAt && parseFloat(compareAt) > parseFloat(price)) {
      priceHtml =
        '<span class="price"><span class="price--price-details price--discounted-price-details">' +
        '<del><span class="amount from">' +
        money(compareAt) +
        '</span></del>' +
        '<ins><span class="amount discounted">' +
        money(price) +
        '</span></ins>' +
        '</span></span>';
    } else {
      priceHtml =
        '<span class="price"><span class="price--price-details"><span class="amount">' +
        money(price) +
        '</span></span></span>';
    }

    var priceCents = Math.round(parseFloat(price) * 100) || '';
    var compareCents = compareAt ? Math.round(parseFloat(compareAt) * 100) : '';

    // Datora wrapper only when the discount app is enabled (mirrors the Liquid).
    var priceBlock = useDatora
      ? '<div class="product-card__datora-wrapper" data-datora-pid="' +
        productId +
        '" data-datora-vid="' +
        variantId +
        '" data-datora-tags="' +
        escapeHtml(tags) +
        '" data-datora-price="' +
        priceCents +
        '" data-datora-compare="' +
        compareCents +
        '" data-datora-spgid="false">' +
        priceHtml +
        '<div class="datora-card-badge" data-datora hidden></div>' +
        '</div>'
      : priceHtml;
    // Titles come as "Base – Flavour" (e.g. "MPS Bar Proteinriegel – Double
    // Chocolate"). Show only the base as the title; the flavour goes on the grey
    // line below it, where enrichVariantLabels appends the pack-size variant label
    // → "Double Chocolate - 12er Pack".
    var titleParts = splitTitleFlavor(title);
    var baseTitle = titleParts.base;
    var flavorFromTitle = titleParts.flavor;
    var titleBlock =
      '<a href="' +
      url +
      '" title="' +
      escapeHtml(title) +
      '" class="product-card-title">' +
      '<div style="font-weight:700;margin-bottom:5px;">' +
      escapeHtml(baseTitle) +
      '</div>' +
      '<div class="product-card-flavor" data-variant-label-handle="' +
      escapeHtml(handle) +
      '" data-variant-label-id="' +
      escapeHtml(variantId) +
      '" data-variant-label-only="true" data-variant-label-prefix="' +
      escapeHtml(flavorFromTitle) +
      '"' +
      (flavorFromTitle ? '' : ' hidden') +
      '>' +
      escapeHtml(flavorFromTitle) +
      '</div>' +
      '<div style="font-size:0.8rem;' +
      (ratingCount === 0 ? 'margin-bottom:5px;' : '') +
      '">' +
      escapeHtml(type) +
      '</div>' +
      '</a>';

    // Mirror product-quick-view-button.liquid (quick-view variant): the white notch
    // wrapper + accent circle button anchored to the image's bottom-right corner.
    // Sold-out products keep the "Ausverkauft" badge but swap the add-to-cart button
    // for a round "notify me when back in stock" button in the same spot.
    var isBundle = isBundleHit(hit);
    var isSoldOut = hit.outofstock && !isBundle;
    // The corner slot holds the notify button when sold out, otherwise the normal
    // add-to-cart / quick-view button (which needs a handle).
    var cornerButton = isDisableAtc(hit)
      ? unavailableButton('product-card--add-to-cart-button--unavailable-corner')
      : isSoldOut
        ? bisButton(hit, 'qlf-bis-btn--corner')
        : handle
          ? isVariantHit(hit)
            ? '<div class="qlf-add-to-cart">' + directAddToCartButton(variantId, '') + '</div>'
            : isBundle
              ? quickViewButton(variantId, handle, '')
              : '<div class="qlf-add-to-cart qlf-add-to-cart--resolve">' +
                resolvingAddToCartButton(handle, variantId, '') +
                '</div>'
          : '';
    var buyButton = cornerButton
      ? '<div class="product-card--add-to-cart--button-wrapper">' +
        '<div class="product-card--add-to-cart--button-wrapper--inner product-card--add-to-cart--button-wrapper-inner">' +
        cornerButton +
        '</div>' +
        '</div>'
      : '';

    // square/portrait/landscape are sized by CSS; 'adapt' has no fixed ratio, so
    // start as a square box and refine --padding-bottom from the loaded image
    // (Meilisearch carries no image dimensions) — see applyAdaptRatios().
    var isAdapt = aspect === 'adapt';
    var ratioExtra = isAdapt ? ' style="--padding-bottom:100%;" data-adapt-ratio' : '';

    var info =
      (priceBefore ? priceBlock : '') + titleBlock + ratingHtml + (priceBefore ? '' : priceBlock);

    return (
      '<product-card class="' +
      cardClasses +
      '" data-productid="' +
      productId +
      '" data-product-id="' +
      productId +
      '" data-variant-id="' +
      variantId +
      '" data-collection-ids="' +
      (hit.collection_ids || '') +
      '">' +
      '<figure class="product-featured-image">' +
      productBadges(hit, money) +
      '<a href="' +
      url +
      '" title="' +
      escapeHtml(title) +
      '" class="product-featured-image-link aspect-ratio aspect-ratio--' +
      aspect +
      '"' +
      ratioExtra +
      '>' +
      '<img class="product-primary-image" src="' +
      image +
      '" alt="' +
      escapeHtml(title) +
      '" loading="lazy"></a>' +
      productCardBadges(hit) +
      '</figure>' +
      buyButton +
      '<div class="product-card-info">' +
      info +
      '</div>' +
      '</product-card>'
    );
  }

  // For 'adapt' aspect ratio: set each card image's box from its real dimensions
  // once loaded, matching the server-computed ratio used by native collection cards.
  function applyAdaptRatios(root) {
    root
      .querySelectorAll('.product-featured-image-link[data-adapt-ratio]')
      .forEach(function (link) {
        var img = link.querySelector('img');
        if (!img) return;
        var set = function () {
          if (img.naturalWidth > 0) {
            link.style.setProperty(
              '--padding-bottom',
              ((img.naturalHeight / img.naturalWidth) * 100).toFixed(2) + '%'
            );
          }
        };
        if (img.complete) set();
        else img.addEventListener('load', set);
      });
  }

  function sortByAvailability(hits) {
    // In-stock first; keep Meilisearch relevance order within each group
    // (Array.prototype.sort is stable in all current browsers).
    return hits.slice().sort(function (a, b) {
      return (a.outofstock ? 1 : 0) - (b.outofstock ? 1 : 0);
    });
  }

  // When a popular-search term is "pinned" to a specific product (its menu link
  // points at a product), the drawer collapses its results to just that product.
  // Set when such a term is clicked, cleared as soon as the visitor types.
  var pinnedProductHandle = null;

  function handleFromUrl(url) {
    var part = String(url || '').split('products/')[1];
    return part ? part.split('?')[0].split('#')[0].toLowerCase() : '';
  }

  function renderHits(container, hits, query, opts) {
    opts = opts || {};
    var filtered = sortByAvailability(filterNegativeHits(hits, query));
    // Pinned term: show only the targeted product (one card, even if the index
    // splits variants into separate docs). Drawer only — never the results page.
    if (!opts.full && pinnedProductHandle) {
      filtered = filtered
        .filter(function (hit) {
          return handleFromUrl(hit.product_url) === pinnedProductHandle;
        })
        .slice(0, 1);
    }
    if (!filtered.length) {
      container.innerHTML =
        opts.loading && query
          ? '<div class="search-loading" aria-live="polite" aria-label="Suche laeuft"><span class="search-loading__spinner"></span></div>'
          : query
            ? '<div class="search-empty">Keine Ergebnisse gefunden für "' +
              escapeHtml(query) +
              '". Überprüfe die Schreibweise oder versuche es mit einer anderen Suchanfrage.</div>'
            : '';
      return;
    }
    var html;
    if (opts.full) {
      // Results page: collection-style grid (looks like collection pages).
      var cols = opts.columns || 4;
      var cards = filtered
        .map(function (h) {
          return '<li class="product">' + productCardFull(h) + '</li>';
        })
        .join('');
      html =
        '<section class="search-products">' +
        '<h3 class="search-results__heading">Produkte</h3>' +
        '<ul class="products collection grid small-up-2 medium-up-' +
        cols +
        '">' +
        cards +
        '</ul>' +
        '</section>';
    } else {
      // Predictive drawer: compact cards.
      html =
        '<div class="predictive-product-card-list">' +
        filtered.map(productCard).join('') +
        '</div>';
    }
    if (opts.showCount) {
      html +=
        '<small>' +
        filtered.length +
        ' ' +
        (filtered.length === 1 ? 'Produkt' : 'Produkte') +
        ' gefunden</small>';
    }
    container.innerHTML = html;
    enrichVariantLabels(container);
    if (opts.full) applyAdaptRatios(container);
    document.dispatchEvent(new CustomEvent('predictive-search:loaded', { bubbles: true }));
    if (typeof window.updateDatoraDiscounts === 'function') window.updateDatoraDiscounts();
  }

  // --------------------------------------------- magnifier toggle (open the drawer)
  // Bound via delegation on `document` and attached SYNCHRONOUSLY as soon as this
  // script runs — not on DOMContentLoaded. The magnifier is an <a href="/search">;
  // until a handler intercepts the click it would navigate to /search. Delegation +
  // synchronous binding means an early click (before the libs, or even before the
  // rest of the page, have loaded) opens the drawer instead of leaving the theme.
  // The drawer/input are resolved at click time, so this is robust to parse order.
  var quickSearchBound = false;
  function initQuickSearchTrigger() {
    if (quickSearchBound) return;
    quickSearchBound = true;
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest && e.target.closest('.thb-quick-search');
      if (!trigger) return;
      e.preventDefault();
      var container = document.getElementById('Search-Drawer');
      if (!container) return;
      document.body.classList.toggle('open-cc');
      container.classList.toggle('active');
      if (container.classList.contains('active')) {
        var input = container.querySelector('input[type="search"]');
        setTimeout(function () {
          if (input) input.focus({ preventScroll: true });
        }, 100);
        document.dispatchEvent(new CustomEvent('search:open', { bubbles: true }));
      }
    });
  }

  // --------------------------------------------- drawer submit plumbing (ported from old class)
  function initDrawerUI() {
    var container = document.getElementById('Search-Drawer');
    if (!container) return;
    var form = container.querySelector('form.searchform');
    var input = container.querySelector('input[type="search"]');
    var submitBtn = container.querySelector('#search-results-submit');

    // Typing a real query clears any pinned term. Programmatic input events from a
    // term click don't fire keydown, so the pin survives the click that sets it.
    if (input)
      input.addEventListener('keydown', function () {
        pinnedProductHandle = null;
      });

    if (submitBtn && form)
      submitBtn.addEventListener('click', function () {
        form.submit();
      });
    if (form && input)
      form.addEventListener('submit', function (e) {
        if (!input.value.trim()) e.preventDefault();
      });

    container.addEventListener('click', function (e) {
      var termLink = e.target.closest && e.target.closest('[data-search-term]');
      if (!termLink) return;
      e.preventDefault();

      var term = termLink.getAttribute('data-search-term') || termLink.textContent || '';
      term = term.trim();
      if (!term || !input) return;

      // A pinned popular-search term collapses the drawer to one product (see renderHits).
      var pinnedHandle = (termLink.getAttribute('data-pinned-handle') || '').trim().toLowerCase();
      pinnedProductHandle = pinnedHandle || null;

      input.value = term;
      input.focus({ preventScroll: true });

      var hasTabs = container.querySelector('.side-panel-content--has-tabs');
      var results = container.querySelector('.searchresults');
      if (!hasTabs || !results) {
        window.location.href = termLink.href;
        return;
      }
      if (hasTabs) hasTabs.classList.add('active');
      if (results && !drawerSearchStarted) {
        results.innerHTML =
          '<div class="search-loading" aria-live="polite" aria-label="Suche laeuft"><span class="search-loading__spinner"></span></div>';
      }

      input.dispatchEvent(new Event('input', { bubbles: true }));
      loadLibs()
        .then(initDrawer)
        .catch(function (err) {
          console.error('[qlf-search]', err);
        });
    });
  }

  // --------------------------------------------------- add-to-cart (delegated, bound once)
  var cartBound = false;
  function bindAddToCart() {
    if (cartBound) return;
    cartBound = true;
    document.addEventListener('click', function (e) {
      var wrapper = e.target.closest && e.target.closest('.qlf-add-to-cart');
      if (!wrapper) return;
      var button = wrapper.querySelector('button');
      if (!button || !e.target.closest('button')) return;
      e.preventDefault();
      var overlay = wrapper.querySelector('.loading-overlay svg');
      var text = wrapper.querySelector('.single-add-to-cart-button--text');
      var success = wrapper.querySelector('.single-add-to-cart-button--success');
      if (overlay) overlay.style.opacity = 1;
      if (text) text.style.display = 'none';

      function resetLoading() {
        if (overlay) overlay.style.opacity = 0;
        if (text) text.style.display = 'block';
      }

      function addVariant(variantId) {
        var body = { items: [{ id: variantId, quantity: 1 }] };
        return fetch((window.Shopify ? Shopify.routes.root : '/') + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
      }

      function finish(res) {
        resetLoading();
        button.classList.add('is-added');
        if (success) success.style.display = 'block';
        setTimeout(function () {
          button.classList.remove('is-added');
          if (text) text.style.display = 'block';
          if (success) success.style.display = 'none';
        }, 1200);
        document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: res }));
        var openCart = function () {
          document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
        };
        if (wrapper.closest('.predictive-product-card')) setTimeout(openCart, 550);
        else openCart();
      }

      if (wrapper.classList.contains('qlf-add-to-cart--resolve')) {
        fetchProduct(button.dataset.productHandle)
          .then(function (product) {
            var variants = Array.isArray(product.variants) ? product.variants : [];
            var available = variants.filter(function (variant) {
              return variant.available;
            });
            if (available.length === 1) return addVariant(available[0].id).then(finish);
            resetLoading();
            openQuickViewFromButton(button);
          })
          .catch(function () {
            resetLoading();
            openQuickViewFromButton(button);
          });
        return;
      }

      addVariant(button.dataset.productid).then(finish).catch(resetLoading);
    });
  }

  // --------------------------------------------------------------- lazy lib loader
  var libsPromise = null;
  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = resolve;
      s.onerror = function () {
        reject(new Error('Failed to load ' + src));
      };
      document.head.appendChild(s);
    });
  }
  function loadLibs() {
    if (libsPromise) return libsPromise;
    libsPromise = loadScript(cfg.instantsearchUrl).then(function () {
      return loadScript(cfg.instantMeilisearchUrl);
    });
    return libsPromise;
  }

  function makeSearchClient() {
    var ns = window.instantMeiliSearch;
    var factory = typeof ns === 'function' ? ns : ns && ns.instantMeiliSearch;
    if (typeof factory !== 'function') {
      throw new Error('instant-meilisearch factory not found on window');
    }
    var res = factory(cfg.host, cfg.apiKey, {
      finitePagination: true,
      meiliSearchParams: { attributesToHighlight: [] }
    });
    return res.searchClient;
  }

  // ----------------------------------------------------------------- drawer (predictive)
  var drawerSearchStarted = false;
  function initDrawer() {
    if (drawerSearchStarted) return;
    var input = document.getElementById('side-panel-search-input');
    var results = document.querySelector('#Search-Drawer .searchresults');
    if (!input || !results) return;
    drawerSearchStarted = true;
    var hasTabs = document.querySelector('#Search-Drawer .side-panel-content--has-tabs');

    var search = window.instantsearch({
      indexName: cfg.productsIndex,
      searchClient: makeSearchClient()
    });
    var c = window.instantsearch.connectors;

    function setActive(q) {
      if (hasTabs) hasTabs.classList.toggle('active', !!(q && q.trim()));
    }

    search.addWidgets([
      window.instantsearch.widgets.configure({ hitsPerPage: DRAWER_HITS }),
      c.connectSearchBox(function (renderOpts, isFirst) {
        if (isFirst) {
          input.addEventListener(
            'input',
            debounce(function (e) {
              renderOpts.refine(e.target.value);
              setActive(e.target.value);
            }, 200)
          );
          if (input.value) {
            renderOpts.refine(input.value);
            setActive(input.value);
          }
        }
      })(),
      c.connectHits(function (renderOpts, isFirstRender) {
        var query = (renderOpts.results && renderOpts.results.query) || '';
        renderHits(results, renderOpts.hits, query, { showCount: true, loading: isFirstRender });
      })()
    ]);

    search.start();
    bindAddToCart();
  }

  // -------------------------------------------------- article cards (Magazin / Rezepte)
  function stripHtml(html) {
    var tmp = document.createElement('div');
    tmp.innerHTML = html || '';
    return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
  }

  function formatArticleDate(value) {
    if (!value) return '';
    var date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('de-DE', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(date);
  }

  function articleCard(hit, opts) {
    opts = opts || {};
    var url = hit.url || (hit.handle ? '/blogs/' + hit.handle : '#');
    var title = hit.title || '';
    var img = imgResize(hit.image_url, 300);
    var summary = stripHtml(hit.summary_html);
    var date = opts.showDate ? formatArticleDate(hit.published_at) : '';
    var meta = [];
    if (opts.showRecipeMeta) {
      if (hit.recipe_preparation_time) meta.push(hit.recipe_preparation_time);
      if (hit.recipe_difficulty) meta.push(hit.recipe_difficulty);
    }
    if (summary.length > 120) summary = summary.slice(0, 120).replace(/\s+\S*$/, '') + '…';

    return (
      '<li class="search-article-item">' +
      '<a class="search-article" href="' +
      url +
      '" title="' +
      escapeHtml(title) +
      '">' +
      (img
        ? '<span class="search-article__image"><img src="' + img + '" alt="" loading="lazy"></span>'
        : '') +
      '<span class="search-article__body">' +
      '<span class="search-article__title">' +
      escapeHtml(title) +
      '</span>' +
      (date
        ? '<time class="search-article__date" datetime="' +
          escapeHtml(hit.published_at) +
          '">' +
          escapeHtml(date) +
          '</time>'
        : '') +
      (meta.length
        ? '<span class="search-article__meta">' + meta.map(escapeHtml).join(' · ') + '</span>'
        : '') +
      (summary ? '<span class="search-article__summary">' + escapeHtml(summary) + '</span>' : '') +
      '</span>' +
      '</a></li>'
    );
  }

  function renderArticles(container, hits, heading, opts) {
    if (!container) return;
    if (!hits.length) {
      container.innerHTML = '';
      return;
    }
    opts = opts || {};
    container.innerHTML =
      '<section class="search-articles">' +
      '<h3 class="search-results__heading search-articles__heading">' +
      escapeHtml(heading) +
      '</h3>' +
      '<ul class="search-articles__grid">' +
      hits
        .map(function (hit) {
          return articleCard(hit, opts);
        })
        .join('') +
      '</ul>' +
      '</section>';
  }

  // Derive the article index names from the products index so the env prefix
  // (e.g. "stage_") is honoured: stage_products → stage_recipes / stage_fitness_magazin.
  function articleIndexNames() {
    var prefix = (cfg.productsIndex || 'products').replace(/products$/, '');
    return { recipes: prefix + 'recipes', magazin: prefix + 'fitness_magazin' };
  }

  // ----------------------------------------------------------------- search results page
  function initSearchPage(query) {
    var container = document.querySelector('.search-results-products');
    if (!container) return;
    var columns = parseInt(container.getAttribute('data-columns'), 10) || 4;
    var magazinEl = document.querySelector('.search-results-magazin');
    var recipesEl = document.querySelector('.search-results-recipes');
    var idx = articleIndexNames();

    var search = window.instantsearch({
      indexName: cfg.productsIndex,
      searchClient: makeSearchClient()
    });
    var c = window.instantsearch.connectors;
    var w = window.instantsearch.widgets;

    var q = query || '';

    // Products first. Query is set via configure() on every index — NOT a search box
    // that refines during init (an init-time refine with child index() widgets throws
    // "Cannot read properties of null (reading 'state')").
    search.addWidgets([
      w.configure({ hitsPerPage: PAGE_HITS, query: q }),
      c.connectHits(function (renderOpts, isFirstRender) {
        renderHits(container, renderOpts.hits, q, {
          showCount: true,
          full: true,
          columns: columns,
          loading: isFirstRender
        });
      })()
    ]);
    search.start();
    bindAddToCart();

    // Wire an article index ONLY if it exists — Meilisearch multi-search fails
    // atomically, so querying a not-yet-synced index would break product results too.
    function addArticleIndex(el, indexName, heading, opts) {
      if (!el) return;
      indexExists(indexName).then(function (ok) {
        if (!ok) return;
        search.addWidgets([
          w.index({ indexName: indexName }).addWidgets([
            w.configure({ hitsPerPage: ARTICLE_HITS, query: q }),
            c.connectHits(function (renderOpts) {
              renderArticles(el, renderOpts.hits, heading, opts);
            })()
          ])
        ]);
      });
    }
    addArticleIndex(magazinEl, idx.magazin, 'Magazin', { showDate: true });
    addArticleIndex(recipesEl, idx.recipes, 'Rezepte', { showRecipeMeta: true });
  }

  // Cheap existence/health probe for an index (404 index_not_found → false).
  var indexExistsCache = {};
  function indexExists(name) {
    if (indexExistsCache[name]) return indexExistsCache[name];
    indexExistsCache[name] = fetch(
      cfg.host.replace(/\/+$/, '') + '/indexes/' + encodeURIComponent(name) + '/search',
      {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + cfg.apiKey, 'Content-Type': 'application/json' },
        body: JSON.stringify({ q: '', limit: 0 })
      }
    )
      .then(function (r) {
        return r.ok;
      })
      .catch(function () {
        return false;
      });
    return indexExistsCache[name];
  }

  // --------------------------------------------------------------------- bootstrap
  function boot() {
    initDrawerUI(); // drawer submit plumbing (toggle is already armed synchronously)
    BIS.bind(); // back-in-stock popup (delegated, fires for drawer + results cards)

    var input = document.getElementById('side-panel-search-input');
    if (input) {
      var initOnce = function () {
        input.removeEventListener('focus', initOnce);
        loadLibs()
          .then(initDrawer)
          .catch(function (e) {
            console.error('[qlf-search]', e);
          });
      };
      input.addEventListener('focus', initOnce);
    }

    if (document.querySelector('.search-results-products')) {
      var q = new URLSearchParams(window.location.search).get('q');
      if (q)
        loadLibs()
          .then(function () {
            initSearchPage(q);
          })
          .catch(function (e) {
            console.error('[qlf-search]', e);
          });
    }
  }

  // Arm the magnifier toggle the instant this script runs, before DOMContentLoaded,
  // so a quick click can never fall through to the anchor's /search navigation.
  initQuickSearchTrigger();

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();
